import BLRequest from 'backendless-request'

export const FileScope = {
  WORKSPACE: 'WORKSPACE',
  FLOW: 'FLOW',
  EXECUTION: 'EXECUTION',
} as const

export type FileScope = (typeof FileScope)[keyof typeof FileScope]

// The scope namespace as handlers receive it (`filesScope` in the bag), so author code names a layer instead
// of spelling the string: `files.list({ scope: filesScope.WORKSPACE })`.
export type FileScopes = typeof FileScope

type FileScopePath = 'workspace' | 'flow' | 'execution'

function toScopePath(scope: FileScope): FileScopePath {
  return scope.toLowerCase() as FileScopePath
}

type CollectionFn = (flowId: string | null, executionId: string | null) => string
type FileFn = (filename: string, flowId: string | null, executionId: string | null) => string

const collectionPathByScope: Record<FileScopePath, CollectionFn> = {
  workspace: () => '/workspace',
  flow: flowId => {
    if (!flowId) {
      throw new Error('Flow scope requires flowId')
    }

    return `/flow/${ flowId }`
  },
  execution: (flowId, executionId) => {
    if (!flowId || !executionId) {
      throw new Error('Execution scope requires flowId and executionId')
    }

    return `/flow/${ flowId }/execution/${ executionId }`
  },
}

const filePathByScope: Record<FileScopePath, FileFn> = {
  workspace: filename => `/workspace/${ filename }`,
  flow: (filename, flowId) => {
    if (!flowId) {
      throw new Error('Flow scope requires flowId')
    }

    return `/flow/${ flowId }/${ filename }`
  },
  execution: (filename, flowId, executionId) => {
    if (!flowId || !executionId) {
      throw new Error('Execution scope requires flowId and executionId')
    }

    return `/flow/${ flowId }/execution/${ executionId }/${ filename }`
  },
}

export interface FlowrunnerContextFilesOptions {
  serverURL: string
  appId: string
  apiKey: string
  //
  runtimeKey: string
  //
  flowId?: string | null
  executionId?: string | null
  //
  retrieveNewS3Token: (runtimeKey: string) => Promise<{
    filesS3Token: string
  }>
}

export interface UploadFileOptions {
  scope?: FileScope
  filename?: string
  ttl?: number
  generateUrl?: boolean
  objectTtl?: number
  overwrite?: boolean
}

export interface ListOptions {
  scope?: FileScope
  ttl?: number
  generateUrl?: boolean
  limit?: number
  cursor?: string
}

export interface GetOptions {
  scope?: FileScope
  ttl?: number
  generateUrl?: boolean
}

export interface DeleteOptions {
  scope?: FileScope
}

export interface UploadResult {
  key: string
  filename: string
  url: string
  size: number
  urlExpiresAt: string
  objectExpiresAt: string | null
  overwritten: boolean
}

export interface FileInfo {
  key: string
  filename: string
  url: string
  size: number
  createdAt: string
  urlExpiresAt: string
  objectExpiresAt: string | null
}

export interface ListResult {
  files: FileInfo[]
  cursor: string | null
  hasMore: boolean
}

export interface DeleteResult {
  key: string
  freedBytes: number
  deleted: boolean
}

export interface UsageResult {
  workspaceId: string
  usedBytes: number
  quotaBytes: number
  usedFormatted: string
  quotaFormatted: string
  utilizationPercent: number
  fileCount: number
}

// The file-service contract as HANDLERS see it (`ctx.files`) — the five methods, nothing about transport.
// `FlowrunnerContextFiles` below is the implementation that talks to the console; a host may supply its
// own via `runServiceMethod({ createFiles })`, which is how an e2e harness points file storage at a local
// S3 without the runner knowing. Keep this in step with the class.
export interface FilesApi {
  uploadFile(file: Buffer | Uint8Array, options?: UploadFileOptions): Promise<UploadResult>
  list(options?: ListOptions): Promise<ListResult>
  get(filename: string, options?: GetOptions): Promise<FileInfo>
  delete(filename: string, options?: DeleteOptions): Promise<DeleteResult>
  usage(): Promise<UsageResult>
}

function cleanParams(params: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== null)
  )
}

// Minimal shape of the node `form-data` instance BLRequest.FormData produces at
// runtime — its append(name, value, options) signature differs from the DOM type.
type MultipartForm = {
  append: (name: string, value: unknown, options?: { filename?: string }) => void
}

function appendFormParams(form: MultipartForm, params: Record<string, unknown>): void {
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      form.append(key, String(value))
    }
  }
}

export class FlowrunnerContextFiles implements FilesApi {
  appId: string
  apiKey: string
  runtimeKey: string
  serverURL: string
  flowId: string | null
  executionId: string | null
  retrieveNewS3Token: (runtimeKey: string) => Promise<{
    filesS3Token: string
  }>

  constructor({
                appId,
                apiKey,
                runtimeKey,
                serverURL,
                flowId,
                executionId,
                retrieveNewS3Token,
              }: FlowrunnerContextFilesOptions) {
    this.appId = appId
    this.apiKey = apiKey
    this.runtimeKey = runtimeKey
    this.serverURL = serverURL
    this.flowId = flowId ?? null
    this.executionId = executionId ?? null

    this.retrieveNewS3Token = retrieveNewS3Token
  }

  get base(): string {
    return `${ this.serverURL }/${ this.appId }/${ this.apiKey }/v1/files`
  }

  private collectionPath(scope: FileScopePath): string {
    return this.base + collectionPathByScope[scope](this.flowId, this.executionId)
  }

  private filePath(filename: string, scope: FileScopePath): string {
    return this.base + filePathByScope[scope](encodeURIComponent(filename), this.flowId, this.executionId)
  }

  private async resolveFilesS3Token(): Promise<string> {
    const { filesS3Token } = await this.retrieveNewS3Token(this.runtimeKey)

    return filesS3Token
  }

  private async request<T>(method: string, url: string, body?: unknown, params?: Record<string, unknown>): Promise<T> {
    const token = await this.resolveFilesS3Token()

    const httpMethod = method.toLowerCase() as 'get' | 'post' | 'put' | 'patch' | 'delete'

    let req = BLRequest[httpMethod](url)
      .set('Authorization', `Bearer ${ token }`)

    if (params) {
      req = req.query(cleanParams(params))
    }

    // `.form()` returns a chainable request; `.send()` returns a promise directly.
    // The request instance is itself thenable, so returning it awaits the response.
    if (BLRequest.FormData && body instanceof BLRequest.FormData) {
      return req.form(body as unknown as Record<string, unknown>) as unknown as Promise<T>
    }

    if (body) {
      return req.send(body) as Promise<T>
    }

    return req as unknown as Promise<T>
  }

  async uploadFile(file: Buffer | Uint8Array, options: UploadFileOptions = {}): Promise<UploadResult> {
    const { scope = FileScope.FLOW, filename, ttl, generateUrl = true, objectTtl, overwrite } = options
    const scopePath = toScopePath(scope)

    // BLRequest.FormData is the node `form-data` instance at runtime (see MultipartForm) —
    // cast to that shape to append a Buffer + filename.
    const form = new BLRequest.FormData!() as unknown as MultipartForm

    form.append('file', Buffer.from(file), { filename: filename ?? 'file' })
    appendFormParams(form, { filename, ttl, generateUrl, objectTtl, overwrite })

    return this.request<UploadResult>('POST', this.collectionPath(scopePath), form)
  }

  async list(options: ListOptions = {}): Promise<ListResult> {
    const { scope = FileScope.FLOW, ttl, generateUrl, limit, cursor } = options
    const scopePath = toScopePath(scope)

    return this.request<ListResult>('GET', this.collectionPath(scopePath), undefined, cleanParams({
      ttl,
      generateUrl,
      limit,
      cursor,
    }))
  }

  async get(filename: string, options: GetOptions = {}): Promise<FileInfo> {
    const { scope = FileScope.FLOW, ttl, generateUrl } = options
    const scopePath = toScopePath(scope)

    return this.request<FileInfo>('GET', this.filePath(filename, scopePath), undefined, cleanParams({
      ttl,
      generateUrl,
    }))
  }

  async delete(filename: string, options: DeleteOptions = {}): Promise<DeleteResult> {
    const { scope = FileScope.FLOW } = options

    return this.request<DeleteResult>('DELETE', this.filePath(filename, toScopePath(scope)))
  }

  async usage(): Promise<UsageResult> {
    return this.request<UsageResult>('GET', `${ this.collectionPath('workspace') }/usage`)
  }
}
